import { Controller } from "@hotwired/stimulus";
import { CompositionTracker } from "../utils/composition_tracker";
import { compileRegExp, parseJsonObject } from "../utils/declared_value";
import { DetachGate } from "../utils/detach_gate";
import { inheritsFieldsetDisabled } from "../utils/focus_candidate";
import { FormDataProjection, prepareFormDataProjection } from "../utils/form_data_projection";
import { halfWidthChar } from "../utils/half_width";
import { ListenerSet } from "../utils/listener_set";
import { MorphRenderWatcher } from "../utils/morph_render_watcher";
import { SafeTimeout } from "../utils/safe_timeout";
import { sharedRegistry } from "../utils/shared_registry";

/**
 * Synchronously prepares a new detached form for managed raw-field submission.
 * Registration is idempotent and lasts for this form element in its document;
 * later fields and repeated FormData constructions need no further calls.
 * Connected forms, and forms where `stimeo--input-mask` has synchronized a managed field,
 * are automatic. Call this before constructing FormData for any other detached
 * form, including an existing form that received a managed field only by a DOM
 * move. This observes submission only, without committing edits or releasing
 * raw-field ownership. Discard the form to discard its registration.
 */
export function prepareInputMaskForm(form: HTMLFormElement): void {
  prepareFormDataProjection(form);
}

/** Default placeholder tokens → single-char regex sources (user tokens merge over these). */
const DEFAULT_TOKENS: Readonly<Record<string, string>> = {
  "9": "\\d",
  a: "[A-Za-z]",
  "*": "[A-Za-z0-9]",
};

/**
 * Suffix of the attribute marking the hidden raw-value sink; its value may name the
 * paired input's `id`.
 */
const UNMASK_ATTR = "unmask";

/**
 * The mask that last wrote each sink's raw value: one map per page for every copy of the
 * library, so masks registered by another Stimulus application, or loaded through both the
 * barrel and the per-controller entry, read the same writer.
 */
const rawWriters = sharedRegistry(
  "stimeo-ui.input-mask-raw-writer.registry.v1",
  () => new WeakMap<HTMLInputElement, object>(),
);

/** Which neighbour a deleting keystroke aimed at, when the platform names one. */
type Deletion = "backward" | "forward";

/** Who moved the value: the user editing the field, or the controller re-deciding it. */
type Cause = "edit" | "reconcile";

/** The outcome of applying a fixed mask to a raw string. */
export interface MaskResult {
  /** The formatted (masked) string. */
  readonly masked: string;
  /** The significant characters only (literals/separators removed). */
  readonly unmasked: string;
  /** Whether every token slot in the pattern is filled. */
  readonly complete: boolean;
  /** Per-output-char flag: true where the char fills a token (not a literal). */
  readonly tokenFlags: readonly boolean[];
}

/**
 * The form of `char` this token takes: as typed when the token accepts it, else
 * its half-width form when *that* is what the token accepts, else `null` for a
 * rejected character. Trying the character as typed first keeps a token written
 * for full-width text (or for text an IME leaves as kana) authoritative.
 */
function acceptedForm(regex: RegExp, char: string): string | null {
  if (regex.test(char)) return char;
  const half = halfWidthChar(char);
  return half !== char && regex.test(half) ? half : null;
}

/**
 * Applies a fixed mask `pattern` to `value` using `tokens` (placeholder → regex).
 * Non-matching input characters are rejected (skipped); literals are auto-inserted
 * and a typed literal that matches is consumed. A character an IME confirmed in
 * full-width form fills a token — or consumes a literal — when its half-width
 * form is the one the mask asked for, and the half-width form is what the output
 * carries. Pure and exported for direct testing.
 */
export function applyMask(
  value: string,
  pattern: string,
  tokens: ReadonlyMap<string, RegExp>,
): MaskResult {
  let masked = "";
  let unmasked = "";
  const tokenFlags: boolean[] = [];
  let valueIndex = 0;
  let totalTokens = 0;

  for (const patternChar of pattern) {
    const regex = tokens.get(patternChar);
    if (regex) totalTokens += 1;
    if (valueIndex >= value.length) continue;

    if (regex) {
      // Skip rejected characters until one fills this token (or input runs out).
      let taken: string | null = null;
      while (valueIndex < value.length) {
        taken = acceptedForm(regex, value[valueIndex] ?? "");
        if (taken !== null) break;
        valueIndex += 1;
      }
      if (taken === null) continue;
      masked += taken;
      unmasked += taken;
      tokenFlags.push(true);
      valueIndex += 1;
    } else {
      masked += patternChar;
      tokenFlags.push(false);
      // The literal is already in the output, so the typed character only has to
      // be the one that renders it — in either width.
      if (halfWidthChar(value[valueIndex] ?? "") === halfWidthChar(patternChar)) valueIndex += 1;
    }
  }

  return {
    masked,
    unmasked,
    complete: totalTokens > 0 && unmasked.length === totalTokens,
    tokenFlags,
  };
}

/**
 * The token map a `tokens` declaration selects: the defaults with the declared
 * single-char regex sources compiled over them.
 *
 * A declaration that is not a JSON object leaves the defaults. Within an object,
 * non-string entries are ignored individually. An invalid regex is answered per key:
 * a key that has a default keeps that default, and a key that has none is left
 * out so the character stays a literal. Either way the rest of the mask, and the
 * field, keep working instead of going down with the broken source.
 */
function compileTokens(declaration: string): Map<string, RegExp> {
  const map = new Map<string, RegExp>();
  for (const [key, source] of Object.entries(DEFAULT_TOKENS)) {
    const compiled = compileRegExp(source, "exact");
    if (compiled) map.set(key, compiled);
  }
  for (const [key, source] of Object.entries(parseTokens(declaration))) {
    const compiled = compileRegExp(source, "exact");
    // A broken source declares nothing: the default this key may already carry
    // is what it falls back to, and a key without one holds no token at all.
    if (compiled) map.set(key, compiled);
  }
  return map;
}

/** The `{ token: source }` pairs a declaration holds; anything else reads as none. */
function parseTokens(declaration: string): Record<string, string> {
  const parsed = parseJsonObject(declaration);
  if (parsed === null) return {};

  const tokens: Record<string, string> = {};
  for (const [key, source] of Object.entries(parsed)) {
    if (typeof source === "string") tokens[key] = source;
  }
  return tokens;
}

/** The direction an `input` event deleted in, or `null` when it is not a deletion. */
function deletionOf(event: Event | undefined): Deletion | null {
  const inputType = event instanceof InputEvent ? event.inputType : "";
  if (!inputType.startsWith("delete")) return null;
  // Word, line, and content deletions all name their direction; the ones that
  // name none (a cut, a drag-out) took a selection, so read them as backward.
  return inputType.endsWith("Forward") ? "forward" : "backward";
}

/** The offset of the `n`-th token char in a masked string, or `-1` when there is none. */
function tokenCharOffset(tokenFlags: readonly boolean[], n: number): number {
  let seen = 0;
  for (let i = 0; i < tokenFlags.length; i += 1) {
    if (!tokenFlags[i]) continue;
    seen += 1;
    if (seen === n) return i;
  }
  return -1;
}

/**
 * Headless **input mask** — formats a field in place against a fixed pattern
 * (`9`=digit, `a`=letter, `*`=alphanumeric, others literal), preserving the caret,
 * rejecting invalid characters, and syncing the raw value to a hidden field. No
 * dedicated APG pattern; `stimeo--currency-input` owns money-specific formatting.
 *
 * Markup contract (identifier: `stimeo--input-mask`, on the `<input>`):
 *   <input type="text" inputmode="numeric"
 *          data-controller="stimeo--input-mask"
 *          data-stimeo--input-mask-pattern-value="999-9999"
 *          data-action="input->stimeo--input-mask#format">
 *   <input type="hidden" name="zip" data-stimeo--input-mask-unmask>
 *
 * `change` and `reconcile` dispatch `{ masked, unmasked, complete }`.
 *
 * @remarks
 * Behavior only and **idempotent** — a snapshot of the committed derived output
 * repairs the hooks and the maintained sink while the IME owns the field's text.
 * Page-wide registries coordinate the last writer and each sink's form transport
 * identity. `connect()` re-formats the existing value and is stable across Turbo
 * restore/morph. The controller sits on the `<input>` itself (a void element), so
 * the raw-value sink is not a Stimulus target; it is the
 * `[data-stimeo--input-mask-unmask]` field resolved nearest-first among the
 * associated form's own controls (an explicit pairing by the sink attribute's
 * value naming the input's `id` wins; otherwise the closest ancestor's value-less
 * sink) — when one form holds several masked inputs, wrap each input+sink pair in a
 * container or pair them by `id`.
 *
 * A maintained named sink carries a reserved internal DOM name. Native FormData and
 * submission expose its original name and entry position. A removed source contributes
 * an empty raw entry without committing a draft or changing the maintained DOM value.
 * Consumers find the sink by id or pairing attribute rather than its DOM name.
 *
 * Formatting never steals keys: it rejects disallowed characters silently and keeps
 * the caret on insert, Backspace, and range replacement (WCAG 2.2 3.3.2 / 1.3.5;
 * the expected format is the consumer's `aria-describedby`). The caret anchors on
 * the token slots the text before it fills, so a rejected character leaves it
 * exactly where it was. Backspace and Delete always progress: when the keystroke
 * only removed literals the mask re-inserts, the significant character it aimed at
 * goes instead. Events fired mid-IME-composition are ignored; the confirmed text is
 * formatted once on `compositionend`. A `pattern` / `tokens` / `unmaskToHidden`
 * change that arrives mid-composition leaves the uncommitted text alone and applies
 * with that same format: as the user's `change` when the composition committed
 * text, as `reconcile` when it ended on the text it began with.
 *
 * It reflects `data-mask-complete` / `data-mask-empty`, dispatches
 * `stimeo--input-mask:change` only when a user edit moves the committed value, and
 * `stimeo--input-mask:reconcile` when the controller itself decides that value —
 * a server-rendered or restored value normalized on connection, or a re-format
 * after `pattern` / `tokens` / `unmaskToHidden` changed. A malformed `tokens`
 * declaration falls back to the default tokens, and a single broken source falls
 * back to that key's default: it is parsed once in `tokensValueChanged`, and the
 * hot path only ever sees the validated map. A field the user cannot edit
 * (`readonly`, `disabled`, or inside a disabled `<fieldset>`) keeps the value the
 * page authored — the sink and the state hooks still follow it, but the field
 * itself is never rewritten. The composition listener is released on `disconnect()`.
 */
export class InputMaskController extends Controller<HTMLInputElement> {
  /** The attribute above, in the namespace this controller is registered under. */
  get #unmaskAttribute(): string {
    return `data-${this.identifier}-${UNMASK_ATTR}`;
  }

  static override values = {
    pattern: { type: String, default: "" },
    tokens: { type: String, default: "" },
    unmaskToHidden: { type: Boolean, default: true },
  };
  static actions = ["format"] as const;
  static events = ["change", "reconcile"] as const;

  declare patternValue: string;
  declare tokensValue: string;
  declare unmaskToHiddenValue: boolean;

  /** The value this controller last committed, and the baseline both events compare. */
  #lastValue: string | null = null;
  #started = false;

  /** The sink this instance last wrote a raw value to and still answers for. */
  #rawSink: HTMLInputElement | null = null;
  /**
   * The raw value written to `#rawSink`, so a different value written there since is left
   * alone.
   */
  #rawValue = "";
  #rawSourceForm: HTMLFormElement | null = null;
  readonly #submission = new FormDataProjection();

  /** Validated token map; the hot path never parses the `tokens` declaration. */
  #tokens = compileTokens("");

  /** The field's text when the running composition began. */
  #textBeforeComposition = "";
  /**
   * The declarations the last format applied; `null` while no format stands — none
   * ran yet, or the running composition withdrew the sink it wrote. A composition owes
   * a format at its end exactly while this differs from what the page declares, so a
   * change taken back owes nothing and a pass that changed nothing cannot discard one.
   */
  #formatted: string | null = null;

  /** The derived output last published, kept apart from the IME's unfinished text. */
  #shown: { result: MaskResult | null; empty: boolean } = { result: null, empty: true };

  readonly #edits = new ListenerSet();

  /** Rejects a single invalid typed replacement before native editing deletes its selection. */
  readonly #onBeforeInput = (event: InputEvent): void => {
    const data = event.data;
    if (
      event.defaultPrevented ||
      !event.cancelable ||
      event.inputType !== "insertText" ||
      data === null ||
      Array.from(data).length !== 1 ||
      !this.#started ||
      this.#composition.isComposing(event)
    )
      return;
    const input = this.element;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (
      start === null ||
      end === null ||
      start === end ||
      input.readOnly ||
      input.disabled ||
      inheritsFieldsetDisabled(input) ||
      !input.isConnected ||
      !this.application.element.contains(input) ||
      !(input.getAttribute("data-controller") ?? "").split(/\s+/).includes(this.identifier) ||
      this.patternValue === "" ||
      this.#formatted !== this.#declared ||
      input.value !== this.#lastValue
    )
      return;
    const prefix = applyMask(input.value.slice(0, start), this.patternValue, this.#tokens);
    for (const character of Array.from(this.patternValue).slice(prefix.tokenFlags.length)) {
      const token = this.#tokens.get(character);
      if (token) {
        if (acceptedForm(token, data) === null) event.preventDefault();
        return;
      }
      if (halfWidthChar(data) === halfWidthChar(character)) return;
    }
    event.preventDefault();
  };

  /**
   * Holds mid-composition input so the IME's uncommitted text is never rewritten,
   * and formats the confirmed text once when the composition ends. A composition
   * that ends on the text it began with committed nothing, so a re-format then is
   * the page's: the mask changed while it ran. One that ends without
   * `compositionend` committed nothing either: a re-format the page asked for
   * meanwhile is the page's and runs then; with none held, the field's text waits
   * for whatever formats it next.
   */
  readonly #composition = new CompositionTracker({
    onStart: () => {
      this.#textBeforeComposition = this.element.value;
    },
    onEnd: () => {
      const committedNothing = this.element.value === this.#textBeforeComposition;
      this.#reformat(committedNothing ? "reconcile" : "edit");
    },
    onAbandon: () => {
      if (this.#formatted !== this.#declared) this.#reformat("reconcile");
    },
  });

  /** Re-parses the declaration and re-formats under the tokens it now selects. */
  tokensValueChanged(): void {
    this.#tokens = compileTokens(this.tokensValue);
    if (this.#started) this.#reformat("reconcile");
  }

  patternValueChanged(): void {
    if (this.#started) this.#reformat("reconcile");
  }

  /** Takes back the raw value it stops maintaining so a submit cannot carry it stale. */
  unmaskToHiddenValueChanged(): void {
    if (!this.#started) return;
    if (!this.unmaskToHiddenValue) this.#withdraw();
    this.#reformat("reconcile");
  }

  /**
   * Takes back the raw value of a synchronization that stopped. A sink withdrawn
   * mid-composition is written again only by a format, so the composition's end owes
   * one even when synchronization comes back before it.
   */
  #withdraw(): void {
    if (this.#retractRaw() && this.#composition.isComposing()) this.#formatted = null;
  }

  readonly #morph = new MorphRenderWatcher(() => this.#reformat("reconcile"));

  /** Tells an in-page move or a Turbo carry from the field leaving the page. */
  readonly #gate = new DetachGate();

  /** The second look for a sink that arrives after the field connected. */
  readonly #adoption = new SafeTimeout();

  override connect(): void {
    this.#gate.cancel();
    this.#morph.observe(this.element);
    this.#started = true;
    this.#composition.observe(this.element);
    this.#edits.add(this.element, "beforeinput", this.#onBeforeInput);
    if (!this.unmaskToHiddenValue) this.#withdraw();
    // A field that moved here with its conversion keeps the value it committed; the
    // conversion's end formats it, and a format the move asks for waits for that end.
    if (this.#composition.isComposing()) {
      this.#reformat("reconcile");
      return;
    }
    // Seed the baseline from the DOM so a value that is already masked stays
    // silent, then re-format any server-rendered/restored value.
    this.#lastValue = this.element.value;
    this.#reformat("reconcile");
    // A sink Turbo carries to the next page is put back later in the task that connected
    // the field; look for it once more on the next task.
    if (this.unmaskToHiddenValue && this.#rawSink === null) {
      this.#adoption.set(() => this.#adoptSink(), 0);
    }
  }

  override disconnect(): void {
    this.#morph.disconnect();
    this.#started = false;
    this.#edits.dispose();
    this.#composition.disconnect();
    this.#adoption.clearAll();
    // A move, a morph or a Turbo carry connects the field again; a field that does not
    // come back has left the page, including the page a Turbo visit leaves.
    this.#gate.disconnected(this, () => this.#retractRaw());
  }

  /** Formats the field on input, preserving the caret. Bound via `data-action`. */
  format(event?: Event): void {
    if (this.#composition.isComposing(event as InputEvent | undefined)) return;
    this.#reformat("edit", deletionOf(event));
  }

  /**
   * Core reformat: mask the current value, restore the caret, sync the sink and
   * the state hooks, and report a moved value under the event `cause` selects.
   *
   * @stimeoRenderRoot
   */
  #reformat(cause: Cause, deletion: Deletion | null = null): void {
    // The IME owns the text until it commits; the composition's end re-formats it. A
    // format asked for meanwhile waits for that end, and the outputs show the last
    // format again.
    if (this.#composition.isComposing()) {
      this.#showAgain();
      return;
    }
    this.#formatted = this.#declared;
    const input = this.element;
    const raw = input.value;

    // No pattern → act as a pass-through. Without this branch `applyMask` would
    // produce an empty string and blank a misconfigured field's value; the hooks
    // still describe the field so a pattern removed at runtime cannot leave a
    // stale `data-mask-complete` behind, and a raw value written under the pattern
    // is taken back, as nothing maintains it any more.
    if (this.patternValue === "") {
      this.#retractRaw();
      this.#shown = { result: null, empty: raw.length === 0 };
      this.#flag("data-mask-complete", false);
      this.#flag("data-mask-empty", raw.length === 0);
      this.#lastValue = raw;
      return;
    }

    const caret = input.selectionStart ?? raw.length;
    let anchor = this.#significantBefore(raw.slice(0, caret));
    let result = applyMask(raw, this.patternValue, this.#tokens);

    // A deletion that only took literals the mask re-inserts would land back on
    // the value it started from, so that keystroke could never delete anything:
    // drop the significant character it aimed at instead.
    if (deletion !== null && result.masked === this.#lastValue) {
      const target = deletion === "backward" ? anchor : anchor + 1;
      const offset = tokenCharOffset(result.tokenFlags, target);
      if (offset >= 0) {
        anchor = target - 1;
        result = applyMask(
          result.masked.slice(0, offset) + result.masked.slice(offset + 1),
          this.patternValue,
          this.#tokens,
        );
      }
    }

    // The value of a field the user cannot edit belongs to the page: publish the
    // derived outputs against it, but never rewrite it and never report a move.
    if (input.readOnly || input.disabled || inheritsFieldsetDisabled(input)) {
      this.#publish(result, raw);
      this.#lastValue = raw;
      return;
    }

    if (input.value !== result.masked) {
      input.value = result.masked;
      this.#restoreCaret(result.tokenFlags, anchor);
    }
    this.#publish(result, result.masked);

    if (result.masked === this.#lastValue) return;
    this.#lastValue = result.masked;
    const detail = { masked: result.masked, unmasked: result.unmasked, complete: result.complete };
    if (cause === "edit") this.dispatch("change", { detail });
    else this.dispatch("reconcile", { detail });
  }

  /** The declarations a format applies. */
  get #declared(): string {
    return JSON.stringify([this.patternValue, this.tokensValue, this.unmaskToHiddenValue]);
  }

  /** Syncs the raw-value sink and the state hooks for the `shown` field text. */
  #publish(result: MaskResult, shown: string): void {
    this.#shown = { result, empty: shown.length === 0 };
    if (this.unmaskToHiddenValue) {
      const sink = this.#resolveSink();
      if (sink !== this.#rawSink) this.#retractRaw();
      if (sink) {
        sink.value = result.unmasked;
        rawWriters.set(sink, this);
        this.#rawSink = sink;
        this.#rawValue = result.unmasked;
        this.#rawSourceForm = this.element.form;
        this.#submission.hold(sink, (value) => this.#submittedValue(sink, value));
      }
    }
    this.#flag("data-mask-complete", result.complete);
    this.#flag("data-mask-empty", shown.length === 0);
  }

  /**
   * Restores the committed hooks and the same sink this instance still writes,
   * without reading the composing field or changing which sink it maintains.
   *
   * @stimeoRenderRoot
   */
  #showAgain(): void {
    const shown = this.#shown;
    const sink = this.#rawSink;
    if (
      shown.result !== null &&
      this.unmaskToHiddenValue &&
      sink !== null &&
      rawWriters.get(sink) === this &&
      this.#resolveSink() === sink
    ) {
      sink.value = shown.result.unmasked;
    }
    this.#flag("data-mask-complete", shown.result?.complete ?? false);
    this.#flag("data-mask-empty", shown.empty);
  }

  /**
   * Takes back the raw value this instance last wrote: empties that sink, wherever the
   * resolution now points, and stops answering for it. The sink keeps a value another
   * mask has written there since, and a different value the page has written there; an
   * instance that has written none leaves every sink as it is. Answers whether it gave
   * up a sink it still answered for.
   */
  #retractRaw(): boolean {
    const sink = this.#rawSink;
    this.#rawSink = null;
    this.#submission.release();
    if (sink === null || rawWriters.get(sink) !== this) return false;
    rawWriters.delete(sink);
    if (sink.value === this.#rawValue) sink.value = "";
    return true;
  }

  /** Projects only its own unchanged raw entry; a page or another writer keeps its value. */
  #submittedValue(sink: HTMLInputElement, value: FormDataEntryValue): FormDataEntryValue {
    if (rawWriters.get(sink) !== this || sink.value !== this.#rawValue || value !== this.#rawValue)
      return value;
    const source = this.element;
    const listed = (source.getAttribute("data-controller") ?? "")
      .split(/\s+/)
      .includes(this.identifier);
    const associated =
      source.form === null ? this.#rawSourceForm === null : source.form === sink.form;
    const eligible =
      source.isConnected &&
      this.application.element.contains(source) &&
      listed &&
      this.unmaskToHiddenValue &&
      associated;
    return eligible ? value : "";
  }

  /** Writes a sink that arrived after the field connected without one. */
  #adoptSink(): void {
    if (this.#resolveSink() !== null) this.#reformat("reconcile");
  }

  /**
   * How many token slots the text before the caret fills. Masking that prefix is
   * what makes a rejected character — or a literal that also matches a token —
   * count exactly as the rendering counts it, so the caret cannot drift.
   */
  #significantBefore(prefix: string): number {
    return applyMask(prefix, this.patternValue, this.#tokens).unmasked.length;
  }

  /** Places the caret after the `n`-th token char (skipping following literals). */
  #restoreCaret(tokenFlags: readonly boolean[], n: number): void {
    let position: number;
    if (n <= 0) {
      // Sit after any leading literals, before the first token slot.
      let i = 0;
      while (i < tokenFlags.length && !tokenFlags[i]) i += 1;
      position = i;
    } else {
      const offset = tokenCharOffset(tokenFlags, n);
      if (offset < 0) {
        position = tokenFlags.length;
      } else {
        let j = offset + 1;
        while (j < tokenFlags.length && !tokenFlags[j]) j += 1;
        position = j;
      }
    }
    try {
      this.element.setSelectionRange(position, position);
    } catch {
      /* selection unsupported for this input type — value formatting still applies */
    }
  }

  /**
   * The hidden raw-value sink for this input, resolved so several masked inputs
   * can coexist in one form:
   *
   * 1. **Explicit pairing** — a sink whose attribute value names this input's
   *    `id` (`data-stimeo--input-mask-unmask="zip"`).
   * 2. **Nearest container** — otherwise, walking up from the input, the first
   *    *value-less* sink in the closest ancestor. Wrapped input+sink pairs each
   *    find their own sink, and a single form-level sink is found the same
   *    way. A sink claimed by another input's id is never matched here.
   *
   * Both steps only ever consider the associated form's own controls, so a sink
   * belonging to a different form (or to none) is never written, while one the
   * `form` attribute associates from elsewhere in the document still resolves —
   * for either the input or the sink, neither of which needs to contain or be
   * contained by the form. A form-less input reads the document instead.
   */
  #resolveSink(): HTMLInputElement | null {
    const candidates = this.#sinkCandidates();
    const id = this.element.id;
    if (id.length > 0) {
      const paired = candidates.find((sink) => sink.getAttribute(this.#unmaskAttribute) === id);
      if (paired) return paired;
    }

    // A bare (value-less) attribute is unclaimed, so a sink naming another
    // input's id can never be taken by this fallback.
    const free = candidates.filter((sink) => sink.getAttribute(this.#unmaskAttribute) === "");
    for (let node = this.element.parentElement; node !== null; node = node.parentElement) {
      const ancestor = node;
      const sink = free.find((candidate) => ancestor.contains(candidate));
      if (sink) return sink;
    }
    return null;
  }

  /**
   * Every sink the input's form owns, in document order — one collection read per
   * resolution, so the cost tracks the form's own controls and not the depth of
   * the markup around it. `form.elements` lists controls the `form` attribute
   * associates from anywhere in the document, and lists nothing another form owns.
   */
  #sinkCandidates(): HTMLInputElement[] {
    const form = this.element.form;
    if (form === null) {
      return Array.from(
        document.querySelectorAll<HTMLInputElement>(`input[${this.#unmaskAttribute}]`),
      );
    }
    return Array.from(form.elements).filter(
      (element): element is HTMLInputElement =>
        element instanceof HTMLInputElement && element.hasAttribute(this.#unmaskAttribute),
    );
  }

  /** Sets a boolean `data-*` flag to `"true"` when `on`, else removes it. */
  #flag(name: string, on: boolean): void {
    if (on) this.element.setAttribute(name, "true");
    else this.element.removeAttribute(name);
  }
}
